import { useState, useEffect } from 'react';

// Material-UI
import {
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell as _TableCell,
} from '@mui/material';
import { Card, CardHeader, CardContent } from '@mui/material';
import { styled, CircularProgress, Typography, Box, Button } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Utility
import fetchExpresso from 'utility/fetchExpresso';
import moment from 'moment-timezone';
import { getRelativeTime } from 'utility/dates';

export default function StripeStatus() {
    const [state, setState] = useState({
        data: null,
        loading: true,
        error: false,
    });

    const { data, loading, error } = state;

    useEffect(() => {
        const tz = moment.tz.guess();

        fetchExpresso(`/admin/reports/stripe_status?tz=${tz}`)
            .then(async (res) => {
                if (res.status !== 200) {
                    setState((s) => ({ ...s, loading: false, error: true }));
                    return;
                }

                const stripeErrorCodes = await res.json();

                let totalErrorCodes = 0;

                for (const errCode of stripeErrorCodes) {
                    totalErrorCodes += errCode.error_count;
                }

                for (const errCode of stripeErrorCodes) {
                    errCode.percent = Number(
                        ((errCode.error_count / totalErrorCodes) * 100).toLocaleString(undefined, {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                        }),
                    );
                }

                setState((s) => ({ ...s, data: stripeErrorCodes, loading: false, error: false }));
            })
            .catch(() => {
                setState((s) => ({ ...s, loading: false, error: true }));
            });
    }, []);

    if (loading) {
        return (
            <Root>
                <Status>
                    <CircularProgress disableShrink />
                </Status>
            </Root>
        );
    }

    if (error) {
        return (
            <Root>
                <Status>
                    <Typography>Unable to load stats</Typography>
                </Status>
            </Root>
        );
    }

    return (
        <Root>
            <CardHeader
                title="Stripe Error Codes"
                action={<Button style={{ textDecoration: 'underline' }}>48 Hrs</Button>}
            />

            <CardContent>
                <TableContainer component={Box}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCellBold>CODE</TableCellBold>
                                <TableCellBold align="right">#</TableCellBold>
                                <TableCellBold align="right">%</TableCellBold>
                                <TableCellBold align="right">
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="flex-end"
                                    >
                                        WHEN
                                        <ArrowDropDownIcon fontSize="small" />
                                    </Box>
                                </TableCellBold>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {data.map((e) => (
                                <TableRow key={e.error_code}>
                                    <TableCell>{e.error_code}</TableCell>
                                    <TableCell align="right">{e.error_count}</TableCell>
                                    <TableCell align="right">{e.percent}%</TableCell>
                                    <TableCell width={30} align="right">
                                        {getRelativeTime(e.last_error, 'short')}
                                    </TableCell>
                                </TableRow>
                            ))}

                            {data.length === 0 && (
                                <TableRow>
                                    <TableCell align="center" colSpan={5}>
                                        <Typography variant="subtitle2">No data found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Root>
    );
}

const Root = styled(Card)({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
});

const Status = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
});

const TableCellBold = styled(_TableCell)(({ theme }) => ({
    fontWeight: 'bold',
}));

const TableCell = styled(_TableCell)(({ theme }) => ({
    whiteSpace: 'nowrap',
}));
